import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BASE_URL from '../BASEURL';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const GallerySlider = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/galleries`);
        if (!response.ok) {
          throw new Error('Failed to fetch galleries');
        }
        const data = await response.json();
        setGalleries(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, []);

  // Auto-play carousel when 3 or more items
  useEffect(() => {
    if (!autoPlay || galleries.length < 3) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleries.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [autoPlay, galleries.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + galleries.length) % galleries.length);
    setAutoPlay(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % galleries.length);
    setAutoPlay(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  if (loading) {
    return (
      <div className="w-full h-96 bg-gray-200 rounded-2xl flex items-center justify-center">
        <div className="text-gray-600">Loading gallery...</div>
      </div>
    );
  }

  if (error || galleries.length === 0) {
    return null;
  }

  // If 3 or more items, show carousel slider
  if (galleries.length >= 3) {
    const currentGallery = galleries[currentIndex];
    const isImage = !currentGallery.gallary_photo.toLowerCase().endsWith('.pdf');

    return (
      <div className="relative w-full h-64 md:h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
        <AnimatePresence mode="wait">
          {isImage ? (
            <motion.img
              key={currentIndex}
              src={`${currentGallery.gallary_photo}`}
              alt={currentGallery.gallary_name}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              onError={(e) => {
                e.target.src = '/placeholder-image.jpg';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <div className="text-center">
                <svg className="w-24 h-24 mx-auto mb-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                <p className="text-white text-lg">{currentGallery.gallary_name}</p>
              </div>
            </div>
          )}
        </AnimatePresence>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

        {/* Title */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold">{currentGallery.gallary_name}</h3>
        </motion.div>

        {/* Previous Button */}
        <button
          onClick={handlePrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 z-10 backdrop-blur-sm"
          aria-label="Previous image"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 z-10 backdrop-blur-sm"
          aria-label="Next image"
        >
          <ChevronRight size={24} />
        </button>

        {/* Dot Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10 flex-wrap justify-center">
          {galleries.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setAutoPlay(false);
              }}
              className={`h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-gold w-8'
                  : 'bg-white/40 w-3 hover:bg-white/60'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>

        {/* Counter */}
        <div className="absolute top-6 right-6 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
          {currentIndex + 1} / {galleries.length}
        </div>
      </div>
    );
  }

  // If less than 3 items, show grid layout
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      {galleries.map((gallery) => (
        <motion.div
          key={gallery._id}
          variants={itemVariants}
          className="group relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 bg-white"
        >
          <div className="relative h-64 overflow-hidden bg-gray-900">
            {gallery.gallary_photo.toLowerCase().endsWith('.pdf') ? (
              <a
                href={`${gallery.gallary_photo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-full flex items-center justify-center bg-gray-800 hover:bg-gray-700 transition-colors"
              >
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                  <p className="text-white text-sm font-medium">View PDF</p>
                </div>
              </a>
            ) : (
              <a
                href={`${gallery.gallary_photo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-full overflow-hidden"
              >
                <motion.img
                  src={`${gallery.gallary_photo}`}
                  alt={gallery.gallary_name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 cursor-pointer"
                  whileHover={{ scale: 1.1 }}
                  onError={(e) => {
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
              </a>
            )}
          </div>

          {/* Title Overlay */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
          >
            <h3 className="text-white font-semibold text-lg">{gallery.gallary_name}</h3>
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default GallerySlider;

